import { DropdownButtonContext } from '@dixa/lib-shared-habla';
import { memo, useContext, useEffect, useRef } from 'react';

import { useCopilotActionRunner } from 'components/copilotActions';

import { CustomAiDraftContent } from '../customAiDraft';

interface EditorCopilotDropdownQuickAiDraftProps {
  csid?: string;
}

/**
 * Represents the "quick Mim draft" action in the copilot dropdown. It runs
 * once the default agent preference resolves and paints its progress in the
 * editor, so the dropdown is only a mounting host. Falls back to the agent
 * picker when there is no default agent to draft with, since that panel can
 * resolve the missing precondition instead of leaving the user stuck. Also
 * falls back to it when an attachment blocks drafting.
 */
export const EditorCopilotDropdownQuickAiDraft = memo(
  function EditorCopilotDropdownQuickAiDraft({
    csid,
  }: EditorCopilotDropdownQuickAiDraftProps) {
    const dropdownContext = useContext(DropdownButtonContext);
    const { run, hasDefaultDraftAgent, hasUnsupportedAttachment } =
      useCopilotActionRunner({
        context: { actions: ['quickAiDraft'] },
        text: '',
        csid,
        // Closing the dropdown unmounts this component while the request is
        // still in flight, so it must not be cancelled.
        abortOnUnmount: false,
      });

    // Whether the default agent is set is fetched asynchronously, so this
    // reacts to it becoming known rather than checking only on mount.
    const hasStartedRef = useRef(false);

    useEffect(() => {
      if (
        hasStartedRef.current ||
        !hasDefaultDraftAgent ||
        hasUnsupportedAttachment
      ) {
        return;
      }

      hasStartedRef.current = true;
      dropdownContext.closeDropdown();
      run('quickAiDraft');
    }, [hasDefaultDraftAgent, hasUnsupportedAttachment, dropdownContext, run]);

    if (hasDefaultDraftAgent && !hasUnsupportedAttachment) {
      return null;
    }

    return <CustomAiDraftContent dixaConversationId={csid ?? ''} />;
  },
);
